import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import {
  LayoutDashboard,
  Megaphone,
  Settings,
  HelpCircle,
  LogOut,
  X,
  Menu,
  ChevronDown,
  Store,
  CreditCard,
  MessageSquare,
} from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useDispatch, useSelector } from 'react-redux'
import { logoutThunk } from '@/redux/userSlice'
import type { RootState } from '@/store'
import {
  useGetApplicationsQuery,
  useGetDirectMessageThreadsQuery,
  useGetMyInvitationsQuery,
} from '@/redux/marketerApi'

/** The brand's places. Brand profile, payments and settings live in the account menu. */
const NAV = [
  {
    to: '/',
    label: 'Dashboard',
    icon: LayoutDashboard,
    iconClass: 'icon-dashboard',
    match: (p: string) => p === '/',
  },
  {
    to: '/campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    iconClass: 'icon-campaigns',
    match: (p: string) =>
      p.startsWith('/campaign') ||
      p.startsWith('/collaborations') ||
      p.startsWith('/submissions') ||
      p.startsWith('/invitations'),
  },
  {
    to: '/marketplace',
    label: 'Marketplace',
    icon: Store,
    iconClass: 'icon-settings',
    match: (p: string) =>
      p.startsWith('/marketplace') ||
      p.startsWith('/athlete/') ||
      p.startsWith('/discover-athletes'),
  },
  {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquare,
    iconClass: 'icon-notifications',
    match: (p: string) => p.startsWith('/messages'),
  },
]

export const AppSidebar: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { open, setOpen, toggleSidebar } = useSidebar()
  const user = useSelector((state: RootState) => state.user.currentUser)

  // Unread direct message count for the badge
  const { data: dmData } = useGetDirectMessageThreadsQuery()
  const unreadDMCount = dmData?.threads.reduce((sum, t) => sum + t.unreadCount, 0) ?? 0

  // Pending collaboration invitations addressed to this marketer
  const { data: invitationsData } = useGetMyInvitationsQuery()
  const pendingInvitationsCount = invitationsData?.invitations.length ?? 0

  // Athlete submissions waiting on the brand
  const { data: applications } = useGetApplicationsQuery()
  const pendingSubmissionsCount = applications?.length ?? 0
  // Everything that arrived for the brand to answer lives under Campaigns
  const campaignsBadge = pendingSubmissionsCount + pendingInvitationsCount

  const handleLogout = async () => {
    await dispatch(logoutThunk() as any)
    navigate('/signin')
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  // When collapsed, clicking should expand. When expanded, clicking should collapse.
  const handleToggleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!open) {
      // If collapsed, expand
      setOpen(true)
    } else {
      // If expanded, collapse
      setOpen(false)
    }
  }

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="p-2 flex items-end justify-start">
        <button
          onClick={handleToggleClick}
          aria-label={open ? 'Close sidebar' : 'Open sidebar'}
          className="h-10 w-10 hover:bg-foreground/10 rounded-md transition-colors cursor-pointer flex items-center justify-center"
        >
          {open ? (
            <X className="h-5 w-5 text-foreground" />
          ) : (
            <Menu className="h-5 w-5 text-foreground" />
          )}
        </button>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV.map((item) => {
                const active = item.match(location.pathname)
                const badge =
                  item.to === '/campaigns'
                    ? campaignsBadge
                    : item.to === '/messages'
                      ? unreadDMCount
                      : 0
                const Icon = item.icon
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={active}>
                      <Link
                        to={item.to}
                        className={cn(
                          'relative flex items-center',
                          !open ? 'justify-center' : 'gap-3',
                        )}
                      >
                        <Icon className={`h-4 w-4 ${active ? 'text-black' : item.iconClass}`} />
                        {badge > 0 && (
                          <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[#C0FD72] border border-sidebar" />
                        )}
                        <span>{item.label}</span>
                        {open && badge > 0 && (
                          <span className="ml-auto text-xs font-bold bg-[#C0FD72] text-black rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className={`w-full hover:bg-foreground/10 ${!open ? 'justify-center' : 'justify-start'}`}
                >
                  <div className={`flex items-center w-full ${open ? 'gap-3' : 'justify-center'}`}>
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={user?.avatar || undefined} alt={user?.email || 'User'} />
                      <AvatarFallback>
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    {open && (
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-foreground">
                          {user?.name || user?.email?.split('@')[0] || 'User'}
                        </p>
                        <p className="text-xs text-gray-600 truncate">{user?.email}</p>
                      </div>
                    )}
                    {open && <ChevronDown className="h-4 w-4 text-gray-600 shrink-0" />}
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                className="w-56 bg-card border-foreground/10 text-foreground"
              >
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=brand" className="cursor-pointer">
                    <Megaphone className="h-4 w-4 mr-2" />
                    Brand profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/settings"
                    className="cursor-pointer text-foreground hover:bg-foreground/10 focus:bg-foreground/10"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/payments" className="cursor-pointer">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Payments and billing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer text-foreground hover:bg-foreground/10 focus:bg-foreground/10"
                  onClick={() => navigate('/help')}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 hover:bg-foreground/10 focus:bg-foreground/10 hover:text-red-600"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
